import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/Card';
import { PasswordStrengthIndicator, validatePassword } from '@/components/admin/PasswordStrengthIndicator';
import {
  auth,
  verifyPasswordResetCode,
  confirmPasswordReset,
  signInWithEmailAndPassword,
  callFunction,
} from '@/lib/firebase';
import { Wine, CheckCircle, Lock, Loader } from 'lucide-react';
import { motion } from 'framer-motion';

export const SetupPasswordPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [oobCode, setOobCode] = useState<string | null>(null);
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [success, setSuccess] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Verifica il codice oobCode quando il componente si monta
  useEffect(() => {
    const verifyCode = async () => {
      const code = searchParams.get('oobCode');
      const actionMode = searchParams.get('mode');

      if (!code || actionMode !== 'resetPassword') {
        setErrors({ token: 'Link non valido o scaduto.' });
        setVerifying(false);
        return;
      }

      setOobCode(code);

      try {
        // Verifica il codice e ottieni l'email
        const userEmail = await verifyPasswordResetCode(auth, code);
        setEmail(userEmail);
        setVerifying(false);
      } catch (err: any) {
        console.error('Error verifying reset code:', err);
        setErrors({ token: 'Link non valido o scaduto. Richiedi un nuovo link.' });
        setVerifying(false);
      }
    };

    verifyCode();
  }, [searchParams]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validazione
    const newErrors: Record<string, string> = {};

    if (!validatePassword(password)) {
      newErrors.password = 'La password non soddisfa tutti i requisiti';
    }

    if (password !== confirmPassword) {
      newErrors.confirmPassword = 'Le password non corrispondono';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    if (!oobCode) {
      setErrors({ token: 'Codice mancante' });
      return;
    }

    try {
      setLoading(true);
      setErrors({});

      console.log('Step 1: Confirming password reset...');
      // 1. Conferma il reset della password usando Firebase
      await confirmPasswordReset(auth, oobCode, password);
      console.log('Step 1: Password reset confirmed ✓');

      console.log('Step 2: Activating user via Cloud Function...');
      // 2. Attiva l'utente (cambia status da 'pending' a 'active')
      const activateUser = callFunction<{ email: string }, any>('activateUserAfterPasswordReset');
      await activateUser({ email });
      console.log('Step 2: User activated ✓');

      console.log('Step 3: Signing in with new credentials...');
      // 3. Login automatico con le nuove credenziali
      await signInWithEmailAndPassword(auth, email, password);
      console.log('Step 3: Sign in successful ✓');

      setSuccess(true);
      // Redirect a home dopo 2 secondi
      setTimeout(() => {
        navigate('/');
      }, 2000);
    } catch (error: any) {
      console.error('Error setting up password:', error);
      setErrors({
        submit: error.message || 'Errore durante l\'impostazione della password. Riprova.',
      });
    } finally {
      setLoading(false);
    }
  };

  // Stato: verifica in corso
  if (verifying) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-wine-white flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center"
        >
          <div className="w-20 h-20 bg-wine-purple/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <Loader className="w-12 h-12 text-wine-purple animate-spin" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Verifica link in corso...</h1>
          <p className="text-gray-600">
            Attendi mentre verifichiamo il tuo link di attivazione.
          </p>
        </motion.div>
      </div>
    );
  }

  // Stato: setup completato con successo
  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-wine-white flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center"
        >
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-12 h-12 text-green-600" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Account Attivato!</h1>
          <p className="text-gray-600 mb-4">
            La tua password è stata impostata con successo.
          </p>
          <p className="text-sm text-gray-500">
            Reindirizzamento alla home...
          </p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-wine-white flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-wine-purple to-wine-purple/80 rounded-full mb-4">
            <Wine className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900">MyWine</h1>
          <p className="text-gray-600 mt-1">Imposta la tua password</p>
        </div>

        {/* Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Lock className="w-5 h-5" />
              Attiva il tuo account
            </CardTitle>
            <CardDescription>
              Scegli una password sicura per accedere alla tua cantina digitale
            </CardDescription>
          </CardHeader>

          <CardContent>
            {errors.token ? (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
                <p className="text-red-900">{errors.token}</p>
                <Button
                  variant="primary"
                  onClick={() => navigate('/login')}
                  className="mt-4"
                >
                  Torna al Login
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Password */}
                <Input
                  label="Nuova Password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  error={errors.password}
                  placeholder="Inserisci password sicura"
                  showPasswordToggle
                  disabled={loading}
                  fullWidth
                  required
                />

                {/* Password Strength */}
                {password && <PasswordStrengthIndicator password={password} />}

                {/* Confirm Password */}
                <Input
                  label="Conferma Password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  error={errors.confirmPassword}
                  placeholder="Conferma password"
                  showPasswordToggle
                  disabled={loading}
                  fullWidth
                  required
                />

                {/* Error generale */}
                {errors.submit && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <p className="text-sm text-red-900">{errors.submit}</p>
                  </div>
                )}

                {/* Submit */}
                <Button
                  type="submit"
                  variant="primary"
                  loading={loading}
                  disabled={!password || !confirmPassword}
                  fullWidth
                  size="lg"
                >
                  Attiva Account
                </Button>

                {/* Info */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-900">
                    ℹ️ Dopo aver impostato la password, potrai accedere con la tua email.
                  </p>
                </div>
              </form>
            )}
          </CardContent>
        </Card>

        {/* Footer */}
        <p className="text-center text-sm text-gray-500 mt-6">
          Hai già un account?{' '}
          <button
            onClick={() => navigate('/login')}
            className="text-wine-purple font-semibold hover:underline"
          >
            Accedi
          </button>
        </p>
      </motion.div>
    </div>
  );
};
